import { MenuDietaryCollection } from '../types/menu';

/**
 * Test builder for the `MenuDietaryCollection[]` API contract shape (the
 * `dietaryCollectionsWithRecipeIds` field on a menu collection).
 *
 * This intentionally encodes the canonical set of dietary collections — their
 * slugs, display names and order — because they are part of the experience-api
 * contract that consumers (webapp) and producers (experience-api) both assert
 * against. If experience-api's domain renames or reorders a collection, its
 * controller spec (which compares domain output to a builder-produced expected
 * value) will fail, surfacing the drift.
 */

export const VEGETARIAN_SLUG = 'vegetarian';
export const PLANT_BASED_SLUG = 'plant-based';
export const DAIRY_FREE_SLUG = 'dairy-free';
export const GLUTEN_FREE_SLUG = 'gluten-free';
export const BEEF_AND_PORK_SLUG = 'beef-pork';
export const CHICKEN_SLUG = 'chicken';
export const FISH_SLUG = 'fish';

// Ordered as the menu presents them: most popular first, then by importance.
const INITIAL_DIETARY_COLLECTIONS: ReadonlyArray<{ name: string; slug: string }> = [
  { name: 'Plant-Based', slug: PLANT_BASED_SLUG },
  { name: 'Vegetarian', slug: VEGETARIAN_SLUG },
  { name: 'Dairy Free', slug: DAIRY_FREE_SLUG },
  { name: 'Gluten Free', slug: GLUTEN_FREE_SLUG },
  { name: 'Beef & Pork', slug: BEEF_AND_PORK_SLUG },
  { name: 'Chicken', slug: CHICKEN_SLUG },
  { name: 'Fish', slug: FISH_SLUG },
];

export class MenuCollectionDietaryCollectionsBuilder {
  private dietaryCollections: MenuDietaryCollection[] = INITIAL_DIETARY_COLLECTIONS.map(
    ({ name, slug }) => ({ name, slug, recipeCount: 0, recipeIds: [] }),
  );

  private withRecipeIds(dietaryCollectionSlug: string, recipeIds: string[]) {
    this.dietaryCollections = this.dietaryCollections.map((dietaryCollection) => {
      if (dietaryCollection.slug === dietaryCollectionSlug)
        return { ...dietaryCollection, recipeIds, recipeCount: recipeIds.length };
      return dietaryCollection;
    });
  }

  public withVegetarianRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(VEGETARIAN_SLUG, recipeIds);
    return this;
  }

  public withPlantBasedRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(PLANT_BASED_SLUG, recipeIds);
    return this;
  }

  public withDairyFreeRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(DAIRY_FREE_SLUG, recipeIds);
    return this;
  }

  public withGlutenFreeRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(GLUTEN_FREE_SLUG, recipeIds);
    return this;
  }

  public withBeefAndPorkRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(BEEF_AND_PORK_SLUG, recipeIds);
    return this;
  }

  public withChickenRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(CHICKEN_SLUG, recipeIds);
    return this;
  }

  public withFishRecipeIds(recipeIds: string[]) {
    this.withRecipeIds(FISH_SLUG, recipeIds);
    return this;
  }

  public build(): MenuDietaryCollection[] {
    return this.dietaryCollections;
  }
}
